import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { resolveRowsPerPageOptions } from "../../../utils/tablePageSize";
import { clx } from "../../../utils/Helpers";

import "./_UITable.scss";
import { useDataGridHeaderScrollSync } from "./useDataGridHeaderScrollSync";

const CELL_TEXT_VARIANT_CLASSES = {
  mutedXs: "ui-table__cell--muted-xs",
  "muted-xs": "ui-table__cell--muted-xs",
};

const FIXED_COLUMN_FIELDS = /^(select|actions?|menu|checkbox)$/i;
// Whole-word matches: unanchored, `id` also matches "candidate"/"provider"/"width",
// silently putting those columns in the narrow bucket.
const WIDE_COLUMN_FIELDS = /\b(name|username|title|company|email|description|location|venue|address|client|employee|applicant|role)\b/i;
const COMPACT_COLUMN_FIELDS = /\b(id|status|type|date|time|count|number|fill|amount|total|score|rating)\b/i;
/** Splits camelCase and snake_case so the matchers above compare whole words. */
const toColumnWords = (text) =>
  text.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ");
/** Matches horizontal cell padding: var(--space-30) on each side. */
const TABLE_CELL_PADDING_INLINE = 60;
/** Per-character fallback, used only where canvas measurement is unavailable. */
const HEADER_CHAR_WIDTH = 9;

let headerCanvas = null;
const headerWidthCache = new Map();

/**
 * Width of the header label in the font the header actually renders in.
 * A per-character estimate cannot suit both "AM" (9.3px/char) and
 * "Upload Date Time" (6.0px/char), so measure rather than estimate.
 */
const measureHeaderText = (text) => {
  if (typeof document === "undefined") return text.length * HEADER_CHAR_WIDTH;

  const rootStyle = getComputedStyle(document.documentElement);
  const rawSize = rootStyle.getPropertyValue("--text-label-sm-font-size").trim();
  const fontSize = rawSize.endsWith("rem")
    ? parseFloat(rawSize) * (parseFloat(rootStyle.fontSize) || 16)
    : parseFloat(rawSize) || 12;
  const font = `500 ${fontSize}px ${
    rootStyle.getPropertyValue("--font-family-base").trim() || "sans-serif"
  }`;

  const cacheKey = `${font}|${text}`;
  if (headerWidthCache.has(cacheKey)) return headerWidthCache.get(cacheKey);

  headerCanvas = headerCanvas || document.createElement("canvas");
  const context = headerCanvas.getContext("2d");
  if (!context) return text.length * HEADER_CHAR_WIDTH;

  context.font = font;
  const width = context.measureText(text).width;
  // Web fonts may still be loading; a fallback-font measurement must not stick.
  if (document.fonts?.status === "loaded") headerWidthCache.set(cacheKey, width);
  return width;
};

const getHeaderMinWidth = (column) => {
  const headerName =
    typeof column.headerName === "string" ? column.headerName.trim() : "";
  if (!headerName) return 0;
  return Math.ceil(measureHeaderText(headerName) + TABLE_CELL_PADDING_INLINE);
};

/** Ensure columns are at least wide enough for the full header label. */
const applyHeaderMinWidth = (column) => {
  const headerMinWidth = getHeaderMinWidth(column);
  if (!headerMinWidth) return column;

  const minWidth = Math.max(column.minWidth || column.width || 0, headerMinWidth);
  const next = { ...column, minWidth };

  if (next.width != null && next.width < minWidth) {
    next.width = minWidth;
  }
  if (next.maxWidth != null && next.maxWidth < minWidth) {
    next.maxWidth = minWidth;
  }

  return next;
};

const mergeCellClassName = (cellClassName, variantClassName) => {
  if (!variantClassName) return cellClassName;
  if (typeof cellClassName === "function") {
    return (params) => clx(cellClassName(params), variantClassName);
  }
  return clx(cellClassName, variantClassName);
};

const getColumnAutoSizing = (column) => {
  const field = String(column.field || "");
  const headerName = String(column.headerName || "");
  const lookupText = toColumnWords(`${field} ${headerName}`);

  if (column.type === "actions" || FIXED_COLUMN_FIELDS.test(field)) {
    const width = column.width || column.minWidth || 88;
    return {
      ...column,
      width,
      minWidth: column.minWidth || width,
      maxWidth: column.maxWidth || width,
    };
  }

  if (WIDE_COLUMN_FIELDS.test(lookupText)) {
    const restColumn = { ...column };
    delete restColumn.maxWidth;
    return {
      ...restColumn,
      flex: 1.35,
      minWidth: column.minWidth || 180,
    };
  }

  if (COMPACT_COLUMN_FIELDS.test(lookupText)) {
    const restColumn = { ...column };
    delete restColumn.maxWidth;
    return {
      ...restColumn,
      flex: 0.8,
      minWidth: column.minWidth || 110,
    };
  }

  const restColumn = { ...column };
  delete restColumn.maxWidth;
  return {
    ...restColumn,
    flex: 1,
    minWidth: column.minWidth || 130,
  };
};

const UITable = ({
  rows,
  columns,
  height,
  contentSized,
  pageSize = 10,
  headerHeight = 43,
  rowHeight = 72,
  rowsPerPageOptions,
  extraRowsPerPageOptions,
  disableSelectionOnClick,
  fillAvailableWidth,
  striped,
  className,
  sx,
  hideFooter,
  onRowClick,
  ...props
}) => {
  const mergedRowsPerPageOptions = resolveRowsPerPageOptions(
    rowsPerPageOptions,
    extraRowsPerPageOptions
  );
  const tableRootRef = useRef(null);
  const resolvedColumns = useMemo(
    () =>
      columns.map(({ cellTextVariant, autoFlex, ...column }) => {
        const sizedColumn =
          fillAvailableWidth && column.flex == null && autoFlex !== false
            ? getColumnAutoSizing(column)
            : column;
        const widthAdjustedColumn = applyHeaderMinWidth(sizedColumn);

        return {
          ...widthAdjustedColumn,
          cellClassName: mergeCellClassName(
            widthAdjustedColumn.cellClassName,
            CELL_TEXT_VARIANT_CLASSES[cellTextVariant]
          ),
        };
      }),
    [columns, fillAvailableWidth]
  );

  const autoHeight = Boolean(contentSized);
  // External pagination (UIPaginationBar) hides the DataGrid footer; show every fetched row.
  const resolvedPageSize = hideFooter
    ? Math.max(rows.length, 1)
    : pageSize;
  const documentDir =
    typeof document !== "undefined"
      ? document.documentElement.getAttribute("dir") || "ltr"
      : "ltr";

  useDataGridHeaderScrollSync(tableRootRef, [documentDir, rows, resolvedColumns]);

  // Don't trigger row redirection when the click lands on the selection checkbox cell.
  const handleRowClick = onRowClick
    ? (params, event, details) => {
        if (event?.target?.closest?.(".MuiDataGrid-cellCheckbox")) return;
        onRowClick(params, event, details);
      }
    : undefined;

  return (
    <Box ref={tableRootRef} sx={{ width: "100%", ...(autoHeight ? {} : { height }) }}>
      <DataGrid
        className={clx("ui-table", striped && "ui-table--striped", className)}
        rows={rows}
        columns={resolvedColumns}
        pageSize={resolvedPageSize}
        headerHeight={headerHeight}
        rowHeight={rowHeight}
        rowsPerPageOptions={mergedRowsPerPageOptions}
        disableSelectionOnClick={disableSelectionOnClick}
        autoHeight={autoHeight}
        sx={sx}
        disableColumnMenu
        hideFooter={hideFooter}
        onRowClick={handleRowClick}
        {...props}
      />
    </Box>
  );
};

UITable.propTypes = {
  rows: PropTypes.array,
  columns: PropTypes.array.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** When true, grid height follows row content (no empty area below the last row). */
  contentSized: PropTypes.bool,
  pageSize: PropTypes.number,
  headerHeight: PropTypes.number,
  rowHeight: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  extraRowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  disableSelectionOnClick: PropTypes.bool,
  /** Adds responsive flex sizing to columns that do not already define flex. */
  fillAvailableWidth: PropTypes.bool,
  striped: PropTypes.bool,
  className: PropTypes.string,
  sx: PropTypes.object,
  hideFooter: PropTypes.bool,
  onRowClick: PropTypes.func,
};

UITable.defaultProps = {
  rows: [],
  height: 360,
  contentSized: false,
  headerHeight: 43,
  rowHeight: 72,
  disableSelectionOnClick: true,
  fillAvailableWidth: true,
  striped: true,
  className: undefined,
  sx: undefined,
};

export default UITable;
